
import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { Socio, Producto, ItemVenta, MedioDePago } from '../types';
import Modal from '../components/Modal';

const DashboardScreen: React.FC = () => {
  const { sesionCaja, socios, productos, registrarVenta, reglasPuntos, addSocio, isReadOnly } = useContext(AppContext);
  const ui = useUI();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSocio, setSelectedSocio] = useState<Socio | null>(null);
  const [cart, setCart] = useState<ItemVenta[]>([]);
  const [puntosACanjear, setPuntosACanjear] = useState(0);
  const [medioDePago, setMedioDePago] = useState<MedioDePago>(MedioDePago.EFECTIVO);
  const [isSocioModalOpen, setIsSocioModalOpen] = useState(false);
  const [newSocio, setNewSocio] = useState({ nombre: '', telefono: '', apellido: '', documento: '', email: '', notas: '', estado: 'activo' as 'activo' | 'inactivo' });
  const [isProcessing, setIsProcessing] = useState(false);

  const filteredSocios = useMemo(() => 
    searchTerm
      ? socios.filter(s => 
          s.estado === 'activo' && (
          (s.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
          (s.documento || '').includes(searchTerm) ||
          (s.telefono || '').includes(searchTerm))
        )
      : [],
    [searchTerm, socios]
  );
  
  const total = useMemo(() => cart.reduce((sum, item) => sum + item.precio_unitario * item.cantidad, 0), [cart]);

  const puntosParaCanjeSocio = selectedSocio?.puntos || 0;
  
  const valorPuntosEnARS = useMemo(() => {
    // Asumimos 1 punto = $1 para simplificar.
    return puntosACanjear;
  }, [puntosACanjear]);

  const totalAPagar = useMemo(() => Math.max(0, total - valorPuntosEnARS), [total, valorPuntosEnARS]);

  const puntosGanados = useMemo(() => {
    if (!selectedSocio) return 0;
    const rule = reglasPuntos.find(r => !r.categoria); // Regla general
    if (rule && rule.cada > 0) {
      return Math.floor(totalAPagar / rule.cada) * rule.puntos;
    }
    return 0;
  }, [totalAPagar, selectedSocio, reglasPuntos]);

  const handleAddToCart = (producto: Producto) => {
    if(isReadOnly) return;
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.producto.id === producto.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.producto.id === producto.id
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        );
      } else {
        return [...prevCart, { producto, producto_id: producto.id, cantidad: 1, precio_unitario: producto.precio }];
      }
    });
  };

  const handleRemoveFromCart = (productId: number) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.producto.id === productId);
      if (existingItem && existingItem.cantidad > 1) {
        return prevCart.map(item =>
          item.producto.id === productId
            ? { ...item, cantidad: item.cantidad - 1 }
            : item
        );
      } else {
        return prevCart.filter(item => item.producto.id !== productId);
      }
    });
  };

  const clearVenta = () => {
    setCart([]);
    setSelectedSocio(null);
    setSearchTerm('');
    setPuntosACanjear(0);
    setMedioDePago(MedioDePago.EFECTIVO);
  }

  const handleFinalizarVenta = async () => {
    if (cart.length === 0 || isProcessing || isReadOnly) return;
    
    setIsProcessing(true);
    const success = await registrarVenta({
      socio: selectedSocio || undefined,
      items: cart,
      total: totalAPagar,
      medio_de_pago: medioDePago,
      puntos_ganados: puntosGanados,
      puntos_canjeados: puntosACanjear,
    });
    
    if(success) {
      clearVenta();
    }
    setIsProcessing(false);
  };

  const handleCrearSocio = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
        // Sanitize documento client-side and prevent duplicate documento locally
        const documentoRaw = typeof newSocio.documento === 'string' ? newSocio.documento.trim() : newSocio.documento;
        const documento = documentoRaw ? documentoRaw : null;

        if (documento) {
            const exists = socios.find(s => s.documento === documento);
            if (exists) {
                ui.showModal('Error', 'Ya existe un socio con ese documento.');
                setIsProcessing(false);
                return;
            }
        }

        const payload = { ...newSocio, documento } as Omit<Socio, 'id' | 'creado_por' | 'creado_en' | 'puntos' | 'club_id'>;
        const success = await addSocio(payload);
        if (success) {
          ui.showToast('success', 'Socio creado con éxito');
          setIsSocioModalOpen(false);
          setNewSocio({ nombre: '', telefono: '', apellido: '', documento: '', email: '', notas: '', estado: 'activo' });
        }
    } catch (error: any) {
        ui.showModal('Error', error.message || 'Error inesperado');
    } finally {
        setIsProcessing(false);
    }
  };

  if (!sesionCaja || sesionCaja.estado === 'cerrada') {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">La caja está cerrada.</h2>
        <p className="text-gray-600">Para registrar ventas, por favor, andá a la sección de "Caja" y abrila.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Cobro Rápido</h2>
        
        <div className="mb-4 relative">
          <input
            type="text"
            placeholder="Buscar socio por nombre, documento o teléfono (opcional)"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full p-3 border rounded-md"
          />
          {searchTerm && filteredSocios.length > 0 && (
            <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 max-h-60 overflow-y-auto">
              {filteredSocios.map(socio => (
                <li key={socio.id} onClick={() => { setSelectedSocio(socio); setSearchTerm(''); }} className="p-3 hover:bg-gray-100 cursor-pointer">
                  {socio.nombre} {socio.apellido} ({socio.documento})
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {selectedSocio && (
          <div className="bg-blue-100 p-3 rounded-md mb-4 flex justify-between items-center">
            <div>
              <p className="font-bold text-blue-800">Socio: {selectedSocio.nombre} {selectedSocio.apellido}</p>
              <p className="text-sm text-blue-700">Puntos disponibles: {puntosParaCanjeSocio}</p>
            </div>
            <button onClick={() => setSelectedSocio(null)} className="text-red-600 font-bold text-lg">&times;</button>
          </div>
        )}
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {productos.filter(p => p.estado === 'activo').map(producto => (
            <button 
              key={producto.id} 
              onClick={() => handleAddToCart(producto)} 
              disabled={isReadOnly} 
              className="bg-white border p-2 rounded-md text-center hover:bg-gray-100 disabled:bg-gray-200 disabled:cursor-not-allowed shadow-sm"
            >
              <p className="font-semibold text-sm text-gray-900">{producto.nombre}</p>
              <p className="text-xs text-gray-600">${producto.precio.toLocaleString('es-AR')}</p>
            </button>
          ))}
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Resumen</h3>
        <div className="space-y-2 min-h-[150px]">
          {cart.length === 0 ? (
            <p className="text-gray-500 text-center py-10">Agregá productos a la venta.</p>
          ) : cart.map(item => (
            <div key={item.producto.id} className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-sm">{item.producto.nombre}</p>
                <p className="text-xs text-gray-500">${item.precio_unitario.toLocaleString('es-AR')} x {item.cantidad}</p>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-sm">${(item.precio_unitario * item.cantidad).toLocaleString('es-AR')}</p>
                <button onClick={() => handleRemoveFromCart(item.producto.id)} className="text-red-500 font-bold">&times;</button>
              </div>
            </div>
          ))}
        </div>
        
        {selectedSocio && (
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-gray-700">Canjear Puntos</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min="0"
                max={Math.min(puntosParaCanjeSocio, total)}
                value={puntosACanjear}
                onChange={e => setPuntosACanjear(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                disabled={isReadOnly}
              />
              <span className="font-semibold text-sm w-16 text-right">{puntosACanjear}</span>
            </div>
            <p className="text-xs text-right text-gray-500">Equivalen a un descuento de ${valorPuntosEnARS.toLocaleString('es-AR')}</p>
          </div>
        )}
        
        <div className="border-t pt-4 mt-4">
            <div className="flex justify-between font-bold text-lg text-gray-800">
                <span>Total</span>
                <span>${total.toLocaleString('es-AR')}</span>
            </div>
            {valorPuntosEnARS > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                    <span>Descuento por Puntos</span>
                    <span>-${valorPuntosEnARS.toLocaleString('es-AR')}</span>
                </div>
            )}
            <div className="flex justify-between font-bold text-2xl mt-2 text-blue-600">
                <span>A Pagar</span>
                <span>${totalAPagar.toLocaleString('es-AR')}</span>
            </div>
             {puntosGanados > 0 && (
                <p className="text-center text-sm text-green-600 mt-2">
                    Esta compra suma {puntosGanados} puntos.
                </p>
            )}
        </div>
        
        <div className="border-t pt-4 mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Medio de Pago</label>
          <select value={medioDePago} onChange={e => setMedioDePago(e.target.value as MedioDePago)} className="w-full p-2 border rounded-md" disabled={isReadOnly}>
            <option value={MedioDePago.EFECTIVO}>Efectivo</option>
            <option value={MedioDePago.TRANSFERENCIA}>Transferencia</option>
          </select>
        </div>
        
        <div className="mt-6 flex flex-col gap-2">
          <button 
            onClick={handleFinalizarVenta} 
            disabled={cart.length === 0 || isProcessing || isReadOnly}
            className="w-full bg-blue-600 text-white py-3 rounded-md font-bold hover:bg-blue-700 disabled:bg-gray-400"
          >
            {isProcessing ? 'Procesando...' : 'Finalizar Venta'}
          </button>
          <button onClick={() => setIsSocioModalOpen(true)} disabled={isReadOnly} className="w-full bg-green-600 text-white py-2 rounded-md font-semibold hover:bg-green-700 disabled:bg-gray-400 text-sm">Crear Socio Rápido</button>
        </div>
        
      </div>
       <Modal isOpen={isSocioModalOpen} onClose={() => setIsSocioModalOpen(false)} title="Crear Nuevo Socio">
         <form onSubmit={handleCrearSocio} className="space-y-4">
            <input type="text" value={newSocio.nombre} onChange={e => setNewSocio({...newSocio, nombre: e.target.value})} placeholder="Nombre" required className="w-full p-2 border rounded"/>
            <input type="text" value={newSocio.apellido} onChange={e => setNewSocio({...newSocio, apellido: e.target.value})} placeholder="Apellido" className="w-full p-2 border rounded"/>
            <input type="text" value={newSocio.telefono} onChange={e => setNewSocio({...newSocio, telefono: e.target.value})} placeholder="Teléfono (Obligatorio)" required className="w-full p-2 border rounded"/>
            <input type="text" value={newSocio.documento} onChange={e => setNewSocio({...newSocio, documento: e.target.value})} placeholder="DNI (Obligatorio)" required className="w-full p-2 border rounded"/>
            <input type="email" value={newSocio.email} onChange={e => setNewSocio({...newSocio, email: e.target.value})} placeholder="Email (Obligatorio)" required className="w-full p-2 border rounded"/>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setIsSocioModalOpen(false)} className="bg-gray-300 text-black px-4 py-2 rounded">Cancelar</button>
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" disabled={isProcessing}>
                    {isProcessing ? 'Guardando...' : 'Guardar Socio'}
                </button>
            </div>
         </form>
       </Modal>
    </div>
  );
};

export default DashboardScreen;
